import React from 'react';
import { db, collection, query, where, onSnapshot, Timestamp, orderBy, addDoc, serverTimestamp } from '@/firebase';
import { useAuth } from '@/context/AuthContext';

export interface Notification {
  id: string;
  title: string;
  message: string;
  time: string;
  read: boolean;
  type?: string;
  createdAt?: any;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (title: string, message: string, type?: string, userId?: string) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
}

const NotificationContext = React.createContext<NotificationContextType | undefined>(undefined);

const NOTIFICATION_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3';

let globalAudio: HTMLAudioElement | null = null;

function playSound() {
  if (!globalAudio) {
    globalAudio = new Audio(NOTIFICATION_SOUND_URL);
  }
  globalAudio.currentTime = 0;
  globalAudio.play().catch(err => console.log('Audio play failed:', err));
}

function getLocalReadIds(): string[] {
  try {
    const raw = localStorage.getItem('readNotifs');
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function formatTime(createdAt: any): string {
  if (!createdAt?.toDate) return 'Baru saja';
  const date = createdAt.toDate();
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Baru saja';
  if (diffMins < 60) return `${diffMins} mnt yang lalu`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs} jam yang lalu`;
  return `${Math.floor(diffHrs / 24)} hari yang lalu`;
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  
  React.useEffect(() => {
    let unsubGlobal: () => void;
    let unsubUser: () => void;

    const notificationsRef = collection(db, 'notifications');
    
    // Using two separate queries and merging locally
    let globalNotifs: any[] = [];
    let userNotifs: any[] = [];

    const updateCombined = () => {
      let combined = [...globalNotifs, ...userNotifs];
      
      // Deduplicate by ID
      const seen = new Set();
      combined = combined.filter(n => {
        if (seen.has(n.id)) return false;
        seen.add(n.id);
        return true;
      });

      combined.sort((a, b) => {
        const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return timeB - timeA;
      });
      
      combined = combined.slice(0, 50);

      const readIds = getLocalReadIds();
      
      const mapped: Notification[] = combined.map(n => ({
        id: n.id,
        title: n.title || 'Notifikasi',
        message: n.message || '',
        time: formatTime(n.createdAt),
        read: readIds.includes(n.id),
        type: n.type,
      }));

      setNotifications(mapped);
    };

    const globalQuery = query(
      notificationsRef,
      where('userId', '==', 'all'),
      orderBy('createdAt', 'desc')
    );
    unsubGlobal = onSnapshot(globalQuery, (snapshot) => {
      let playedSound = false;
      const initialLoad = globalNotifs.length === 0;
      globalNotifs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      if (!initialLoad) {
        snapshot.docChanges().forEach((change) => {
           if (change.type === 'added') playedSound = true;
        });
      }
      if (playedSound) playSound();
      updateCombined();
    }, (error) => {
      console.warn("Error subscribing to global notifications:", error);
    });

    if (user?.uid) {
      const userQuery = query(
        notificationsRef,
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      unsubUser = onSnapshot(userQuery, (snapshot) => {
        let playedSound = false;
        const initialLoad = userNotifs.length === 0;
        userNotifs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        if (!initialLoad) {
          snapshot.docChanges().forEach((change) => {
             if (change.type === 'added') playedSound = true;
          });
        }
        if (playedSound) playSound();
        updateCombined();
      }, (error) => {
        console.warn("Error subscribing to user notifications:", error);
      });
    } else {
      userNotifs = [];
      updateCombined();
    }

    return () => {
      if (unsubGlobal) unsubGlobal();
      if (unsubUser) unsubUser();
    };
  }, [user?.uid]);

  // Fallback programmatic add (writes to Firebase)
  const addNotification = React.useCallback(async (title: string, message: string, type: string = 'system', userId: string = 'all') => {
    try {
      await addDoc(collection(db, 'notifications'), {
        title,
        message,
        type,
        userId,
        createdAt: serverTimestamp()
      });
    } catch (err) {
      console.error("Failed to add notification:", err);
    }
  }, []);

  const markAsRead = (id: string) => {
    const readIds = getLocalReadIds();
    if (!readIds.includes(id)) {
      readIds.push(id);
      localStorage.setItem('readNotifs', JSON.stringify(readIds));
      
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, read: true } : n
      ));
    }
  };

  const markAllAsRead = () => {
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    if (unreadIds.length === 0) return;
    
    const readIds = getLocalReadIds();
    localStorage.setItem('readNotifs', JSON.stringify([...readIds, ...unreadIds]));
    
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, addNotification, markAsRead, markAllAsRead }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = React.useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
